// RestModal — take a short or long rest. Short rest lets the party spend hit dice
// to heal; long rest fully restores HP, spell slots, and half the hit-dice pool.
import { useState } from "react";
import { api } from "../lib/api";
import type { PartyMember, RestResult } from "../lib/types";
import { Modal, Spinner } from "./ui";
import { useToast } from "../hooks/useToast";

export function RestModal({
  gameId,
  party,
  onClose,
}: {
  gameId: string;
  party: PartyMember[];
  onClose: () => void;
}) {
  const toast = useToast();
  const [kind, setKind] = useState<"short" | "long">("short");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RestResult | null>(null);

  async function rest() {
    setBusy(true);
    try {
      const res = kind === "short"
        ? await api.shortRest(gameId, {})
        : await api.longRest(gameId, {});
      setResult(res);
      toast.push(kind === "short" ? "Short rest taken" : "Long rest — party restored", "success");
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Rest failed", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2>Make Camp</h2>
      <div className="modal__sub">Recover between the dangers of the deep.</div>

      <div className="rest-choice">
        <button className={`rest-card${kind === "short" ? " on" : ""}`} onClick={() => setKind("short")}>
          <div className="rest-icon">🌙</div>
          <div className="rest-name">Short Rest</div>
          <div className="rest-desc">An hour's respite. Spend Hit Dice to recover HP; some abilities recharge.</div>
        </button>
        <button className={`rest-card${kind === "long" ? " on" : ""}`} onClick={() => setKind("long")}>
          <div className="rest-icon">🌄</div>
          <div className="rest-name">Long Rest</div>
          <div className="rest-desc">Eight hours. Full HP, all spell slots, half your Hit Dice, −1 exhaustion.</div>
        </button>
      </div>

      <div className="rest-party mt16">
        <div className="label">The Party</div>
        {party.map((p) => (
          <div key={p.id} className="rest-member">
            <span>{p.name}</span>
            <span className="muted" style={{ fontFamily: "var(--mono)", fontSize: 13 }}>
              {p.hp}/{p.max_hp} HP
            </span>
          </div>
        ))}
      </div>

      {result && (
        <div className="rest-result mt16">
          <div className="label">{result.kind === "short" ? "Short rest" : "Long rest"} complete</div>
          {result.results.map((r) => (
            <div key={r.character} className="rest-member">
              <span>{r.character}</span>
              <span className="muted" style={{ fontFamily: "var(--mono)", fontSize: 13 }}>
                {r.healed != null && r.healed > 0 ? `+${r.healed} → ` : ""}{r.current_hp}{r.max_hp ? `/${r.max_hp}` : ""} HP
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="modal__row">
        <button className="btn btn--ghost" onClick={onClose}>{result ? "Close" : "Cancel"}</button>
        {!result && (
          <button className="btn btn--primary" disabled={busy} onClick={rest}>
            {busy ? <Spinner /> : kind === "short" ? "Take Short Rest" : "Take Long Rest"}
          </button>
        )}
      </div>
    </Modal>
  );
}
